/**
 * Postnummer Tillæg Widget — v3
 * ==============================
 * - Header-widget (desktop: inline form, mobil: knap + pop-up)
 * - Postnummer gemmes i localStorage i 7 dage (auto-popup-flag i sessionStorage)
 * - Automatisk tillæg pr. produkt i kurven
 * - Deaktiverer køb-knapper uden eller ved ukendt postnummer
 * - Disclaimer med to tilstande (ingen indtastning / ukendt postnummer)
 *
 * OPSÆTNING: Erstat variant ID'erne nedenfor med dine egne.
 */

const PostnummerWidget = {

  // ── Konfiguration ────────────────────────────────────────────────────────
  ZONE2_VARIANT_ID: '57585935188300',
  ZONE3_VARIANT_ID: '57585938694476',

  ZONE1: ['9670','9690','9681','9640','9600','9240','9541','9610'],
  ZONE2: ['9631','9632','9620','9500','9510','9520','9530','9230','9200','9000'],
  ZONE3: ['9210','9260','9220','9270','9575','9400','9430','9440','9490'],

  // ── State ────────────────────────────────────────────────────────────────
  currentPostnummer: null,
  currentZone: null,
  isUpdating: false,

  // ── Storage-hjælper: postnummer gemmes i 7 dage ──────────────────────────
  STORAGE_KEY: 'pn_data',
  STORAGE_DAGE: 7,

  savePostnummer(pn, zone) {
    try {
      const payload = {
        pn: pn,
        zone: zone,
        expires: Date.now() + this.STORAGE_DAGE * 24 * 60 * 60 * 1000,
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
    } catch (e) { /* localStorage utilgængelig — ignorér */ }
  },

  loadPostnummer() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      // Udløbet → ryd og returnér intet
      if (!data.expires || Date.now() > data.expires) {
        localStorage.removeItem(this.STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  },

  // ── Init ─────────────────────────────────────────────────────────────────
  init() {
    const saved = this.loadPostnummer();
    if (saved && saved.pn) {
      this.currentPostnummer = saved.pn;
      this.currentZone       = saved.zone !== undefined && saved.zone !== null
        ? parseInt(saved.zone) : null;
    }

    this.bindHeaderForm();
    this.renderAllHeaderWidgets();
    this.bindPopup();
    this.applyPageState();
    this.watchCart();
    this.interceptBuyForms();
    this.watchAddonLines();
    this.watchVariantChanges();
    this.watchFortovNote();
    this.maybeAutoOpenPopup();

    // Synk ved hver sideindlæsning — fanger fjernelser sket via fuld
    // sideopdatering (fx link-navigation på /cart-siden) som interceptoren
    // ikke kan se. Sikrer at tillæg = antal hovedprodukter ved load.
    this.syncTillæg();
  },

  // Åbn pop-up automatisk ved første besøg hvis intet postnummer er sat.
  // Kun én gang per session, så den ikke bliver påtrængende.
  maybeAutoOpenPopup() {
    if (this.currentPostnummer) return;                       // har allerede postnummer
    if (sessionStorage.getItem('pn_auto_shown') === '1') return; // vist før i denne session
    sessionStorage.setItem('pn_auto_shown', '1');

    // Lille forsinkelse så siden er færdig med at loade først
    setTimeout(() => {
      if (!this.currentPostnummer && typeof this.openPopup === 'function') {
        this.openPopup();
      }
    }, 800);
  },

  // ── Zone-opslag ───────────────────────────────────────────────────────────
  getZone(postnummer) {
    const pn = postnummer.toString().trim();
    if (this.ZONE1.includes(pn)) return 1;
    if (this.ZONE2.includes(pn)) return 2;
    if (this.ZONE3.includes(pn)) return 3;
    return 0;
  },

  // ── Håndter nyt postnummer ────────────────────────────────────────────────
  async handlePostnummer(postnummer) {
    const pn   = postnummer.trim();
    const zone = this.getZone(pn);

    this.currentPostnummer = pn;
    this.currentZone       = zone;

    this.savePostnummer(pn, zone);

    this.renderAllHeaderWidgets();
    this.applyPageState();

    if (zone >= 1) {
      await this.syncTillæg();
    } else {
      await this.clearTillæg();
    }
  },

  // ── Header widget: desktop inline form ───────────────────────────────────
  renderAllHeaderWidgets() {
    document.querySelectorAll('[data-pn-header]').forEach(el => this.renderHeaderWidget(el));
  },

  renderHeaderWidget(container) {
    const pn   = this.currentPostnummer;
    const zone = this.currentZone;

    const zoneLabel = {
      // Zone-badges (1, 2, 3) vises ikke længere i header — fylder for meget.
      // Kun fejl vises, så kunden advares ved postnummer uden for zonerne.
      0: '<span class="pn-badge pn-badge--error">Ikke tilgængeligt</span>',
    };

    const iconSvg = `<svg class="pn-mobile-btn__icon" width="16" height="16" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
      stroke-linejoin="round" aria-hidden="true">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5"/>
    </svg>`;

    const btnLabel = pn
      ? `${iconSvg}<span class="pn-mobile-btn__text">${pn}</span>`
      : `${iconSvg}<span class="pn-mobile-btn__text">Postnummer</span>`;

    container.innerHTML = `
      <form class="pn-header-form" data-pn-header-form>
        <label class="pn-header-label" for="pn-header-input">Postnummer</label>
        <div class="pn-header-row">
          <input
            type="text"
            id="pn-header-input"
            class="pn-header-input"
            placeholder="Indtast postnummer"
            maxlength="4"
            inputmode="numeric"
            autocomplete="postal-code"
            value="${pn || ''}"
            aria-label="Postnummer"
          >
          <button type="submit" class="pn-header-btn">Gem</button>
        </div>
        ${pn && zoneLabel[zone] ? `<div class="pn-header-status">${zoneLabel[zone]}</div>` : ''}
      </form>
      <button class="pn-mobile-btn${pn ? ' pn-mobile-btn--set' : ''}"
        data-pn-mobile-open aria-label="${pn ? 'Rediger postnummer' : 'Indtast postnummer'}">
        ${btnLabel}
      </button>
    `;

    container.querySelector('[data-pn-header-form]').addEventListener('submit', e => {
      e.preventDefault();
      const val = container.querySelector('.pn-header-input').value;
      if (val) this.handlePostnummer(val);
    });

    container.querySelector('[data-pn-mobile-open]').addEventListener('click', () => {
      this.openPopup();
    });
  },

  bindHeaderForm() {
    const obs = new MutationObserver(() => {
      document.querySelectorAll('[data-pn-header]:not([data-pn-bound])').forEach(el => {
        el.setAttribute('data-pn-bound', '1');
        this.renderHeaderWidget(el);
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  },

  // ── Mobil pop-up ──────────────────────────────────────────────────────────
  bindPopup() {
    const overlay = document.getElementById('pn-popup-overlay');
    const form    = document.getElementById('pn-popup-form');
    const closeBtn = document.getElementById('pn-popup-close');
    if (!overlay) return;

    // Luk ved klik på overlay-baggrund
    overlay.addEventListener('click', e => {
      if (e.target === overlay) this.closePopup();
    });

    // Luk-knap
    if (closeBtn) closeBtn.addEventListener('click', () => this.closePopup());

    // Luk ved Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closePopup();
    });

    // Ryd status så snart brugeren begynder at rette feltet
    const popupInput = document.getElementById('pn-popup-input');
    if (popupInput) {
      popupInput.addEventListener('input', () => {
        const status = document.getElementById('pn-popup-status');
        if (status) {
          status.textContent = '';
          status.className   = 'pn-popup__status';
        }
      });
    }

    // Formular submit
    if (form) {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const input  = document.getElementById('pn-popup-input');
        const status = document.getElementById('pn-popup-status');
        if (!input) return;

        const val  = input.value.trim();
        const zone = this.getZone(val);

        // Nulstil altid status før vi sætter en ny — så gammel tekst aldrig hænger ved
        if (status) {
          status.textContent = '';
          status.className   = 'pn-popup__status';
        }

        if (status) {
          if (!val) {
            status.textContent = 'Indtast venligst et postnummer.';
            status.className   = 'pn-popup__status pn-popup__status--error';
            return;
          }
          if (zone === 0) {
            status.textContent = 'Postnummeret er ikke inden for levering. Du kan sende os en besked for et tilbud.';
            status.className   = 'pn-popup__status pn-popup__status--error';
          } else {
            const zoneNames = { 1: 'Zone 1 — ingen tillæg', 2: 'Zone 2 — +625 kr.', 3: 'Zone 3 — +1.250 kr.' };
            status.textContent = '✓ Gemt: ' + zoneNames[zone];
            status.className   = 'pn-popup__status pn-popup__status--ok';
          }
        }

        await this.handlePostnummer(val);

        // Luk pop-up automatisk efter kort pause ved gyldigt postnummer
        // (status ryddes når pop-up'en åbnes næste gang)
        if (zone > 0) setTimeout(() => this.closePopup(), 800);
      });
    }
  },

  openPopup() {
    const overlay = document.getElementById('pn-popup-overlay');
    const input   = document.getElementById('pn-popup-input');
    const status  = document.getElementById('pn-popup-status');
    if (!overlay) return;

    if (input) {
      input.value = this.currentPostnummer || '';
    }
    if (status) {
      status.textContent = '';
      status.className   = 'pn-popup__status';
    }

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Fokus på input-felt
    setTimeout(() => { if (input) input.focus(); }, 50);
  },

  closePopup() {
    const overlay = document.getElementById('pn-popup-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },

  // ── Side-tilstand: deaktiver køb / vis disclaimer ─────────────────────────
  applyPageState() {
    const zone      = this.currentZone;
    const pn        = this.currentPostnummer;
    const noEntry   = !pn;
    const isUnknown = pn && zone === 0;
    const shouldDisable = noEntry || isUnknown;

    document.querySelectorAll('[data-product-buy-btn]').forEach(btn => {
      // Gem original tekst første gang så vi kan gendanne den
      if (!btn.dataset.pnOriginalHtml) {
        btn.dataset.pnOriginalHtml = btn.innerHTML;
      }
      // Respekter temaets egen "udsolgt"-tilstand — overskriv ikke en variant der ikke er tilgængelig
      if (btn.dataset.pnSoldOut === '1') return;

      if (shouldDisable) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('title', noEntry
          ? 'Indtast dit postnummer for at bestille'
          : 'Bestilling ikke tilgængelig i dit postnummer'
        );
        btn.innerHTML = noEntry
          ? 'Indtast postnummer'
          : 'Ikke tilgængelig her';
      } else {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('title');
        btn.innerHTML = btn.dataset.pnOriginalHtml;
      }
    });

    document.querySelectorAll('[data-pn-disclaimer]').forEach(el => {
      if (shouldDisable) {
        el.style.display = 'flex';
        el.setAttribute('data-pn-state', noEntry ? 'no-entry' : 'unknown');
      } else {
        el.style.display = 'none';
        el.removeAttribute('data-pn-state');
      }
    });

    const cartStatus = document.getElementById('pn-status');
    if (cartStatus && pn) {
      if (isUnknown) {
        cartStatus.textContent = 'Postnummer ' + pn + ' er ikke inden for levering.';
        cartStatus.className   = 'pn-status pn-status--error';
      } else if (zone) {
        const labels = {
          1: '✓ Zone 1 — ingen tillæg',
          2: '✓ Zone 2 — +625 kr. pr. produkt',
          3: '✓ Zone 3 — +1.250 kr. pr. produkt',
        };
        cartStatus.textContent = labels[zone];
        cartStatus.className   = 'pn-status pn-status--' + (zone === 1 ? 'ok' : 'zone');
      }
    }

    // Checkout-knapper i /cart og drawer (temaets [data-cart-checkout-cta])
    document.querySelectorAll('[data-cart-checkout-cta]').forEach(cta => {
      if (!cta.dataset.pnOriginalHtml) {
        cta.dataset.pnOriginalHtml = cta.innerHTML;
      }
      if (shouldDisable) {
        cta.classList.add('pn-checkout-blocked');
        cta.setAttribute('aria-disabled', 'true');
        cta.setAttribute('title', noEntry
          ? 'Indtast dit postnummer for at bestille'
          : 'Bestilling ikke tilgængelig i dit postnummer'
        );
        // Hvis det er en knap, brug disabled-property; ellers gøres den uklikbar via CSS
        if (cta.tagName === 'BUTTON') cta.disabled = true;
      } else {
        cta.classList.remove('pn-checkout-blocked');
        cta.removeAttribute('aria-disabled');
        cta.removeAttribute('title');
        if (cta.tagName === 'BUTTON') cta.disabled = false;
      }
    });

    // Vis besked i kurv/drawer hvorfor checkout er blokeret
    document.querySelectorAll('[data-pn-cart-block]').forEach(el => {
      if (shouldDisable) {
        el.style.display = 'flex';
        el.setAttribute('data-pn-state', noEntry ? 'no-entry' : 'unknown');
        if (noEntry) {
          el.innerHTML = '<strong>Indtast dit postnummer</strong>'
            + '<span>Du skal angive dit postnummer i toppen af siden, før du kan gå til betaling.</span>';
        } else {
          el.innerHTML = '<strong>Bestilling ikke tilgængelig i dit postnummer</strong>'
            + '<span>Vi leverer desværre ikke til ' + (pn || 'det valgte postnummer')
            + ' via webshoppen. <a href="/pages/kontakt">Send os en besked for et tilbud →</a></span>';
        }
      } else {
        el.style.display = 'none';
        el.removeAttribute('data-pn-state');
      }
    });

    // Opdater produktprisen til at inkludere leveringstillæg (pris + tillæg)
    this.updateProductPrice();

    this.observeBuyButtons();
    this.guardCheckout();
  },

  // Læg leveringstillæg oveni produktprisen i [data-product-price].
  // Læser grundprisen fra temaets variant-JSON ud fra valgt variant,
  // så vi altid regner ovenpå den korrekte pris (ikke en allerede-tillagt).
  updateProductPrice() {
    const zone = this.currentZone;
    const pn   = this.currentPostnummer;
    const tillægKr = { 1: 0, 2: 625, 3: 1250 };
    const extraCents = (pn && zone > 0) ? (tillægKr[zone] || 0) * 100 : 0;

    // Skjul prisen helt ved intet postnummer eller utilgængeligt postnummer —
    // kunden skal ikke se en pris uden korrekt leveringstillæg.
    const hidePrice = !pn || zone === 0;

    document.querySelectorAll('[data-product-price]').forEach(priceEl => {
      if (hidePrice) {
        priceEl.style.display = 'none';
        return;
      }
      priceEl.style.display = '';

      const scope = priceEl.closest('.product') || document;

      // Find valgt variant + dens grundpris fra temaets JSON
      let variants = [];
      const vScript = scope.querySelector('[data-product-variants]');
      if (vScript) {
        try { variants = JSON.parse(vScript.textContent || '[]'); } catch (e) {}
      }
      const variantId = scope.querySelector('[data-product-variant-id]')?.value;
      let basePrice = null;
      let compareBase = null;
      if (variants.length && variantId) {
        const match = variants.find(v => v.id.toString() === variantId.toString());
        if (match) {
          basePrice = match.price; // i øre
          // compare_at_price kun hvis den er højere end prisen (reelt udsalg)
          if (match.compare_at_price && match.compare_at_price > match.price) {
            compareBase = match.compare_at_price;
          }
        }
      }
      // Fallback: hvis vi ikke kan finde variant, gem den oprindeligt viste pris
      if (basePrice === null) {
        if (priceEl.dataset.pnBasePrice) {
          basePrice = parseInt(priceEl.dataset.pnBasePrice, 10);
          if (priceEl.dataset.pnCompareBase) {
            compareBase = parseInt(priceEl.dataset.pnCompareBase, 10);
          }
        } else {
          return; // ingen pålidelig grundpris — rør ikke
        }
      }
      // Gem grundpriser så fremtidige opdateringer altid regner korrekt
      priceEl.dataset.pnBasePrice = String(basePrice);
      if (compareBase !== null) {
        priceEl.dataset.pnCompareBase = String(compareBase);
      } else {
        delete priceEl.dataset.pnCompareBase;
      }

      const total = basePrice + extraCents;

      // Compare-at-pris (udsalg): læg samme tillæg oveni så priserne matcher
      let compareHtml = '';
      if (compareBase !== null) {
        const compareTotal = compareBase + extraCents;
        compareHtml = '<span class="product__price-compare">'
          + this.formatMoney(compareTotal) + '</span>';
      }

      priceEl.innerHTML = this.formatMoney(total) + (compareHtml ? ' ' + compareHtml : '');
    });
  },

  // Formatér øre til dansk valuta (matcher temaets format)
  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const formatted = amount.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return formatted + ' kr';
  },

  // Lyt på variant-/mængde-skift så vi lægger tillæg oveni igen
  // EFTER temaet har opdateret grundprisen i [data-product-price].
  watchVariantChanges() {
    if (this._variantWatched) return;
    this._variantWatched = true;
    document.querySelectorAll('[data-variant-form]').forEach(form => {
      form.addEventListener('change', () => {
        // Temaets egen handler kører også på 'change'; vent et øjeblik
        // så vores tillæg lægges oveni den friske grundpris.
        setTimeout(() => {
          // Nulstil gemt grundpris så den genlæses fra den nye variant
          document.querySelectorAll('[data-product-price]').forEach(el => {
            delete el.dataset.pnBasePrice;
            delete el.dataset.pnCompareBase;
          });
          this.updateProductPrice();
        }, 50);
      });
    });
  },

  // Obligatorisk valg: kørsel på fortov/indkørsel → styrer ordre-note.
  // Kunden vælger aktivt JA eller NEJ før checkout.
  FORTOV_JA: 'Det BEKRÆFTES hermed at Oudrup gerne må køre på fortov/indkørsel.',
  FORTOV_NEJ: 'Der må IKKE køres ind på fortov/indkørsel.',
  FORTOV_VALUE_JA: 'ja',
  FORTOV_VALUE_NEJ: 'nej',

  watchFortovNote() {
    // Gem noten direkte på kurven via cart/update.js — så den altid ligger
    // på kurven uanset hvorfra der checkes ud (drawer, /cart, re-render).
    const saveNote = (text) => {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-PN-Internal': '1' },
        body: JSON.stringify({
          note: text,
          attributes: {
            Levering: text
          }
        })
      }).catch(() => {});
    };

    const applyNote = (choice) => {
      const normalizedChoice = choice === this.FORTOV_VALUE_JA
        ? this.FORTOV_VALUE_JA
        : this.FORTOV_VALUE_NEJ;
      const text = normalizedChoice === this.FORTOV_VALUE_JA ? this.FORTOV_JA : this.FORTOV_NEJ;
      this._fortovChoice = normalizedChoice;
      this._fortovNote = text;
      // Hold alle valg (drawer + /cart) i sync
      document.querySelectorAll('[data-pn-fortov]').forEach(input => {
        const shouldCheck = input.value === normalizedChoice;
        if (input.checked !== shouldCheck) input.checked = shouldCheck;
      });
      // Spejl også til evt. skjulte note-felter (harmløst hvis de findes)
      document.querySelectorAll('[data-pn-note]').forEach(note => { note.value = text; });
      // Spejl til leverings-attribut så beskeden kan vises i ordrebekræftelsen
      document.querySelectorAll('[data-pn-delivery-note]').forEach(note => { note.value = text; });
      // Gem på kurven
      saveNote(text);
    };

    const getSelectedChoice = () => {
      const selected = document.querySelector('[data-pn-fortov]:checked');
      if (!selected) return null;
      return selected.value === this.FORTOV_VALUE_JA ? this.FORTOV_VALUE_JA : this.FORTOV_VALUE_NEJ;
    };

    // Intet valg skal være forudvalgt ved indlæsning.
    // Valg og note sættes først, når kunden aktivt vælger JA/NEJ.
    const resetFortovChoice = () => {
      document.querySelectorAll('[data-pn-fortov]').forEach(input => {
        if (input.checked) input.checked = false;
      });
      this._fortovChoice = null;
      this._fortovNote = null;
    };
    resetFortovChoice();

    // Lyt på ændringer (delegeret, så det også virker efter re-render)
    document.addEventListener('change', (e) => {
      const input = e.target.closest && e.target.closest('[data-pn-fortov]');
      if (input) applyNote(input.value);
    });

    // Når drawer/kurv re-renderes: genskab valgt radio, men kun hvis
    // kunden allerede har valgt noget i denne visning.
    const obs = new MutationObserver(() => {
      if (!this._fortovChoice) {
        document.querySelectorAll('[data-pn-fortov]').forEach(input => {
          if (input.checked) input.checked = false;
        });
        return;
      }
      document.querySelectorAll('[data-pn-fortov]').forEach(input => {
        const shouldCheck = input.value === this._fortovChoice;
        if (input.checked !== shouldCheck) input.checked = shouldCheck;
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  },

  // Bloker klik på checkout hvis postnummer mangler/ukendt (capture-fase,
  // før temaets egne handlers — også for <a href> der ellers navigerer væk)
  guardCheckout() {
    if (this._checkoutGuarded) return;
    this._checkoutGuarded = true;
    document.addEventListener('click', (e) => {
      const cta = e.target.closest('[data-cart-checkout-cta], [name="checkout"], [href*="/checkout"]');
      if (!cta) return;
      const pn = this.currentPostnummer;
      const block = !pn || this.currentZone === 0;
      if (block) {
        e.preventDefault();
        e.stopPropagation();
        // Åbn postnummer-pop-up så kunden kan rette det med det samme
        if (typeof this.openPopup === 'function') this.openPopup();
      }
    }, true);
  },

  observeBuyButtons() {
    if (this._btnObserver) return;
    this._btnObserver = new MutationObserver(() => {
      // Undgå uendelig løkke: vores egne knap-mutationer skal ikke trigge ny kørsel
      if (this._applyingState) return;
      this._applyingState = true;
      this.applyPageState();
      // Frigiv på næste tick efter DOM-mutationerne er flushet
      requestAnimationFrame(() => { this._applyingState = false; });
    });
    this._btnObserver.observe(document.body, { childList: true, subtree: true });
  },

  // ── Kurv-synkronisering ───────────────────────────────────────────────────
  async getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  countMainProducts(cart) {
    let total = 0;
    for (const item of cart.items) {
      const id = item.variant_id.toString();
      if (id !== this.ZONE2_VARIANT_ID && id !== this.ZONE3_VARIANT_ID) {
        total += item.quantity;
      }
    }
    return total;
  },

  async syncTillæg() {
    if (this.isUpdating) return;
    this.isUpdating = true;
    try {
      const zone    = this.currentZone || 0;
      const cart    = await this.getCart();
      const mainQty = this.countMainProducts(cart);

      const zone2Item = cart.items.find(i => i.variant_id.toString() === this.ZONE2_VARIANT_ID);
      const zone3Item = cart.items.find(i => i.variant_id.toString() === this.ZONE3_VARIANT_ID);
      const z2cur = zone2Item ? zone2Item.quantity : 0;
      const z3cur = zone3Item ? zone3Item.quantity : 0;

      const updates = {};
      // Ingen hovedprodukter → ryd altid begge tillæg (tillæg er bundet til produkt)
      if (mainQty === 0) {
        updates[this.ZONE2_VARIANT_ID] = 0;
        updates[this.ZONE3_VARIANT_ID] = 0;
      } else {
        updates[this.ZONE2_VARIANT_ID] = zone === 2 ? mainQty : 0;
        updates[this.ZONE3_VARIANT_ID] = zone === 3 ? mainQty : 0;
      }

      if (updates[this.ZONE2_VARIANT_ID] !== z2cur || updates[this.ZONE3_VARIANT_ID] !== z3cur) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-PN-Internal': '1' },
          body: JSON.stringify({ updates })
        });
        this.refreshCartUI();
      }
    } finally {
      this.isUpdating = false;
    }
  },

  // Interceptér product buy forms (method=post → fetch så vi kan tilføje tillæg)
  interceptBuyForms() {
    const self = this;

    const bindForm = (form) => {
      if (form._pnBound) return;
      form._pnBound = true;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const zone = self.currentZone;

        // Ingen zone valgt — send normal form submit
        if (!zone || zone === 0) {
          form.submit();
          return;
        }

        const variantId = (form.querySelector('[data-product-variant-id]') || form.querySelector('input[name="id"]'))?.value;
        if (!variantId) { form.submit(); return; }

        const btn = form.querySelector('[data-product-buy-btn]');
        const originalHtml = btn ? (btn.dataset.pnOriginalHtml || btn.innerHTML) : '';
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          btn.innerHTML = 'Lægger i kurv…';
        }

        try {
          // 1. Tilføj hovedprodukt
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          if (!addRes.ok) throw new Error('add failed');

          // 2. Sync tillæg
          await self.syncTillæg();

          // 3. Redirect til kurv (som temaet normalt gør)
          window.location.href = '/cart';

        } catch (err) {
          console.error('pn buy error:', err);
          // Gendan knappen og fald tilbage til normal submit
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            btn.innerHTML = originalHtml;
          }
          form.submit();
        }
      });
    };

    // Bind eksisterende forms
    document.querySelectorAll('[data-product-buy-form]').forEach(bindForm);

    // Bind dynamisk tilføjede forms
    const obs = new MutationObserver(() => {
      document.querySelectorAll('[data-product-buy-form]:not([data-pn-bound])').forEach(bindForm);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  },

  // ── Tillægslinjer i kurv/drawer ───────────────────────────────────────────
  // Tillægsprodukterne skjules HELT, og stk-prisen lægges i stedet oveni
  // det tilhørende hovedprodukts linjepris. Kunden ser altså aldrig
  // tillægsproduktet — kun en samlet pris pr. produktlinje.

  // Tillæg pr. enhed i øre, ud fra valgt zone
  addonUnitCents() {
    const kr = { 1: 0, 2: 625, 3: 1250 };
    if (!this.currentPostnummer || !this.currentZone || this.currentZone < 1) return 0;
    return (kr[this.currentZone] || 0) * 100;
  },

  // Hent kurv-data og byg opslag vi kan bruge til at matche DOM-linjer
  async fetchCartMap() {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const byKey = {};
      const byVariant = {};
      const addonKeys = new Set();
      const addonTitles = new Set();
      (cart.items || []).forEach(item => {
        const vid = item.variant_id.toString();
        if (item.key) byKey[item.key] = item;
        (byVariant[vid] = byVariant[vid] || []).push(item);
        if (vid === this.ZONE2_VARIANT_ID || vid === this.ZONE3_VARIANT_ID) {
          if (item.key) addonKeys.add(item.key);
          if (item.title) addonTitles.add(item.title);
          if (item.product_title) addonTitles.add(item.product_title);
        }
      });
      this._cartByKey = byKey;
      this._cartByVariant = byVariant;
      this._addonKeys = addonKeys;
      this._addonTitles = addonTitles;
      return cart;
    } catch (e) { return null; }
  },

  // Udtræk variant-id og line-key fra et DOM-linje-element
  extractLineIds(line) {
    let key = line.getAttribute('data-line-key')
      || line.querySelector('[data-line-key]')?.getAttribute('data-line-key') || null;
    let variantId = null;

    // Fjern-link: /cart/change?id=VARIANTID:KEY&quantity=0
    const changeLink = line.querySelector('[href*="/cart/change"]');
    if (changeLink) {
      const m = (changeLink.getAttribute('href') || '').match(/[?&]id=([^:&]+)(?::([^&]+))?/);
      if (m) {
        variantId = variantId || m[1];
        key = key || (m[2] ? decodeURIComponent(m[2]) : null);
      }
    }
    // Produkt-link: /products/...?variant=VARIANTID
    const prodLink = line.querySelector('a[href*="variant="]');
    if (prodLink) {
      const m = (prodLink.getAttribute('href') || '').match(/[?&]variant=(\d+)/);
      if (m) variantId = variantId || m[1];
    }
    return { key, variantId };
  },

  // Er linjen et tillægsprodukt?
  isAddonLine(line, ids) {
    if (ids.variantId && (ids.variantId === this.ZONE2_VARIANT_ID || ids.variantId === this.ZONE3_VARIANT_ID)) return true;
    if (ids.key && this._addonKeys && this._addonKeys.has(ids.key)) return true;
    // Fallback: matchende titel (fx "Heraf fragt" hentet fra cart.js)
    const text = (line.textContent || '');
    if (this._addonTitles) {
      for (const t of this._addonTitles) {
        if (t && text.indexOf(t) !== -1) return true;
      }
    }
    // Fallback: billede-filnavn på tillægsproduktet
    if (line.querySelector('img[src*="leveringstillaeg"]')) return true;
    return false;
  },

  // Skjul tillægslinjer + læg tillæg oveni hovedprodukternes linjepris
  decorateCartLines() {
    const unit = this.addonUnitCents();

    const lineSelectors = [
      '[data-line-key]', '.cart-drawer__line', '.cart-line',
      '.cart-item', '.cart__row', '.cart-page__item', '.line-item'
    ];
    const seen = new Set();

    lineSelectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(line => {
        if (seen.has(line)) return;
        seen.add(line);

        // /cart-siden håndteres server-side i Liquid (markeret med
        // data-pn-server-cart). Draweren har ingen markør og dekoreres af JS.
        if (line.closest('[data-pn-server-cart]')) return;

        const ids = this.extractLineIds(line);

        // 1. Tillægslinje → skjul helt
        if (this.isAddonLine(line, ids)) {
          line.style.display = 'none';
          line.setAttribute('data-pn-addon-hidden', '1');
          line.classList.add('pn-line-ready');
          return;
        }

        // 2. Hovedprodukt → læg tillæg oveni linjeprisen
        if (unit <= 0) {
          // Intet tillæg (zone 1 / intet postnummer) → gendan original pris hvis vi har ændret
          this.restoreLinePrice(line);
          line.classList.add('pn-line-ready');
          return;
        }

        // Find kurv-item for at kende antal + grundpris
        let item = null;
        if (ids.key && this._cartByKey && this._cartByKey[ids.key]) {
          item = this._cartByKey[ids.key];
        } else if (ids.variantId && this._cartByVariant && this._cartByVariant[ids.variantId]) {
          item = this._cartByVariant[ids.variantId][0];
        }
        if (!item) { line.classList.add('pn-line-ready'); return; }

        const priceEl = line.querySelector('.cart-line__price, .cart-drawer__line-price');
        if (!priceEl) { line.classList.add('pn-line-ready'); return; }

        // Gem original linjepris (i øre) første gang
        if (!priceEl.dataset.pnBaseLine) {
          priceEl.dataset.pnBaseLine = String(item.final_line_price);
        }
        const baseLine = parseInt(priceEl.dataset.pnBaseLine, 10);
        const newLine = baseLine + unit * item.quantity;
        priceEl.textContent = this.formatMoney(newLine);

        // Marker linjen som klar → afsløres (skjult via CSS indtil nu)
        line.classList.add('pn-line-ready');
      });
    });
  },

  // Gendan oprindelig linjepris (når tillæg ikke længere gælder)
  restoreLinePrice(line) {
    const priceEl = line.querySelector('.cart-line__price, .cart-drawer__line-price');
    if (priceEl && priceEl.dataset.pnBaseLine) {
      priceEl.textContent = this.formatMoney(parseInt(priceEl.dataset.pnBaseLine, 10));
    }
  },

  // Observer: hold kurv-linjer dekoreret når kurven re-renderes
  watchAddonLines() {
    const run = () => {
      this.fetchCartMap().then(() => this.decorateCartLines());
    };
    run();

    // Sikkerhedsnet: afslør altid alle linjer efter 3s, selv hvis noget fejler,
    // så de ikke kan forblive skjult permanent.
    setTimeout(() => {
      document.querySelectorAll('.cart-drawer__line:not(.pn-line-ready)').forEach(l => {
        l.classList.add('pn-line-ready');
      });
    }, 3000);

    let t;
    const obs = new MutationObserver((mutations) => {
      // SYNKRONT: skjul tillægslinjer i SAMME øjeblik de indsættes, før
      // browseren maler dem — ellers glimter "Heraf fragt" når temaets
      // fetchCart() gen-renderer draweren ved åbning.
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          const lines = [];
          if (node.matches && node.matches('.cart-drawer__line')) lines.push(node);
          if (node.querySelectorAll) node.querySelectorAll('.cart-drawer__line').forEach(l => lines.push(l));
          lines.forEach(line => {
            // Hurtig tillægs-detektion uden at vente på cart.js:
            // billede-filnavn eller kendte titler
            const isAddon = line.querySelector('img[src*="leveringstillaeg"]')
              || (this._addonTitles && [...this._addonTitles].some(tt => tt && (line.textContent || '').indexOf(tt) !== -1));
            if (isAddon) {
              line.style.display = 'none';
              line.setAttribute('data-pn-addon-hidden', '1');
              line.classList.add('pn-line-ready');
            }
          });
        }
      }
      // Fuld dekorering (priser + endelig oprydning) debounced bagefter
      clearTimeout(t);
      t = setTimeout(run, 40);
    });
    obs.observe(document.body, { childList: true, subtree: true });

    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', () => setTimeout(run, 60));
    });
  },

  // Opdater kurv-UI efter syncTillæg
  refreshCartUI() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        // Opdater item-count badges
        const count = cart.item_count || 0;
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = String(count);
          el.hidden = !count;
        });

        // Opdater totaler
        const moneyFormat = (() => {
          const drawer = document.querySelector('[data-cart-drawer]');
          return (drawer && drawer.dataset.moneyFormat) || '{{amount}} kr';
        })();
        const formatMoney = (cents) => {
          const amount = (Number(cents) || 0) / 100;
          const formatted = amount.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/g, formatted);
        };
        const total = cart.total_price || 0;
        const vat = Math.round(total * 0.20);
        const subtotalEl = document.querySelector('[data-cart-subtotal]');
        const vatEl      = document.querySelector('[data-cart-vat]');
        const totalEl    = document.querySelector('[data-cart-total]');
        const footerEl   = document.querySelector('[data-cart-footer]');
        if (subtotalEl) subtotalEl.textContent = formatMoney(total);
        if (vatEl)      vatEl.textContent      = formatMoney(vat);
        if (totalEl)    totalEl.textContent    = formatMoney(total);
        if (footerEl)   footerEl.hidden        = !total;

        // Drawer åben: re-render linjerne via temaets fetchCart (åbn-klik)
        const drawer = document.querySelector('[data-cart-drawer]');
        if (drawer && drawer.classList.contains('is-open')) {
          const openBtn = document.querySelector('[data-cart-open]');
          if (openBtn) openBtn.click();
        }

        // På /cart-siden: produktlinjerne er server-renderet og opdateres
        // ikke af tal-opdateringen ovenfor. Hent siden på ny og udskift
        // kurv-indholdet, så den fjernede tillægslinje også forsvinder visuelt.
        if (location.pathname.indexOf('/cart') === 0) {
          this.refreshCartPageLines();
        }

        // Dekorér linjer igen (skjul tillæg + læg pris oveni) efter re-render
        setTimeout(() => this.fetchCartMap().then(() => this.decorateCartLines()), 400);
      })
      .catch(() => {});
  },

  // Genindlæs produktlinjerne på /cart-siden uden fuld reload
  refreshCartPageLines() {
    fetch(location.pathname, { headers: { 'X-PN-Internal': '1' } })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Forsøg en række gængse containere til kurv-linjer
        const selectors = [
          '[data-cart-items]', '.cart-page__items', '.cart__items',
          '.cart-items', 'form[action="/cart"]', '.cart-page', '.cart'
        ];
        for (const sel of selectors) {
          const fresh = doc.querySelector(sel);
          const current = document.querySelector(sel);
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            return;
          }
        }
        // Fallback: hvis vi ikke fandt en kendt container, reload som sidste udvej
        location.reload();
      })
      .catch(() => {});
  },

  async clearTillæg() {
    const updates = {};
    updates[this.ZONE2_VARIANT_ID] = 0;
    updates[this.ZONE3_VARIANT_ID] = 0;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
  },

  // ── Vagt mod manuel fjernelse ─────────────────────────────────────────────
  // Debounce: forhindrer burst af requests
  _syncDebounceTimer: null,
  debouncedSync(delay = 400) {
    clearTimeout(this._syncDebounceTimer);
    this._syncDebounceTimer = setTimeout(() => {
      // Kør altid: enten tilføjes/justeres tillæg (zone 2-3),
      // eller ryddes op hvis hovedprodukter er fjernet (mainQty=0 → tillæg=0)
      this.syncTillæg();
    }, delay);
  },

  // Lyt på reelle cart-ændringer — tillægget følger altid antal hovedprodukter
  watchCart() {
    const cartEvents = [
      'cart:updated', 'cart-updated', 'cart:refresh',
      'cart:change', 'theme:cart:updated', 'CartDrawer:open',
    ];
    cartEvents.forEach(evt => {
      document.addEventListener(evt, () => this.debouncedSync());
      window.addEventListener(evt, () => this.debouncedSync());
    });

    this.interceptCartRequests();
  },

  // Intercepter fetch og XHR til /cart/add.js og /cart/change.js
  interceptCartRequests() {
    const self = this;

    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = (args[0] instanceof Request ? args[0].url : args[0]) || '';
      // Spring vores egne interne tillægs-opdateringer over (undgår løkke)
      const opts = args[1] || {};
      const isInternal = opts.headers && (opts.headers['X-PN-Internal'] || (opts.headers.get && opts.headers.get('X-PN-Internal')));
      const promise = originalFetch.apply(this, args);
      if (!isInternal && /\/cart\/(add|change|update)\.js/.test(url)) {
        promise.then(res => { if (res.ok) self.debouncedSync(300); }).catch(() => {});
      }
      return promise;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._pnUrl = url || '';
      return originalOpen.apply(this, [method, url, ...rest]);
    };
    XMLHttpRequest.prototype.send = function(...args) {
      if (/\/cart\/(add|change|update)\.js/.test(this._pnUrl)) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) self.debouncedSync(300);
        });
      }
      return originalSend.apply(this, args);
    };
  },

};

document.addEventListener('DOMContentLoaded', () => PostnummerWidget.init());